import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Register = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dob: '',
    gender: '',
    address: '',
    preferredContact: '',
    avatar: '',
    role: 'viewer',
    terms: false,
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [pwStrength, setPwStrength] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
    } else if (type === 'file') {
      const file = files[0];
      if (file) {
        if (file.size > 100 * 1024) { // 100KB limit
          setWarning('Avatar image is too large (max 100KB). It will not be saved.');
          setForm({ ...form, avatar: '' });
          setAvatarPreview('');
          return;
        } else {
          setWarning('');
        }
        const reader = new FileReader();
        reader.onload = ev => {
          setForm({ ...form, avatar: ev.target.result });
          setAvatarPreview(ev.target.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setForm({ ...form, [name]: value });
      if (name === 'password') setPwStrength(getPwStrength(value));
    }
    setError('');
  };

  function getPwStrength(pw) {
    if (!pw) return '';
    if (pw.length < 6) return 'Weak';
    if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/.test(pw)) return 'Strong';
    if (/^(?=.*[a-zA-Z])(?=.*\d).{6,}$/.test(pw)) return 'Medium';
    return 'Weak';
  }

  const handleSubmit = e => {
    e.preventDefault();
    // Validation
    if (!form.name || !form.email || !form.phone || !form.password || !form.confirmPassword || !form.dob || !form.gender || !form.address || !form.preferredContact) {
      setError('All fields are required.');
      return;
    }
    if (!/^\d{10,}$/.test(form.phone.replace(/[^\d]/g, ''))) {
      setError('Enter a valid phone number.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!form.terms) {
      setError('You must accept the Terms & Conditions.');
      return;
    }
    // Save registration request to localStorage
    const requests = JSON.parse(localStorage.getItem('registrationRequests') || '[]');
    if (requests.some(r => r.email === form.email)) {
      setError('A registration request with this email already exists.');
      return;
    }
    // Prevent large avatar from being saved
    const registrationData = { ...form };
    if (!form.avatar) {
      delete registrationData.avatar;
    }
    try {
      requests.push({ ...registrationData, status: 'pending', id: Date.now() });
      localStorage.setItem('registrationRequests', JSON.stringify(requests));
      setSuccess(true);
      setForm({
        name: '', email: '', phone: '', password: '', confirmPassword: '', dob: '', gender: '', address: '', preferredContact: '', avatar: '', role: 'viewer', terms: false
      });
      setAvatarPreview('');
      setPwStrength('');
      setWarning('');
    } catch (err) {
      if (err && err.name === 'QuotaExceededError') {
        setError('Registration failed: Storage limit reached. Please contact support or clear old registrations.');
      } else {
        setError('Registration failed: ' + err.message);
      }
    }
  };

  if (success) {
    return (
      <div className="max-w-sm mx-auto my-20 p-8 bg-white rounded-xl shadow-lg text-center">
        <h2 className="text-2xl font-bold mb-6 text-purple-700">Registration Submitted</h2>
        <p>Your registration request has been submitted for approval. You will be notified once approved.</p>
        <button className="mt-6 px-4 py-2 bg-purple-700 text-white rounded" onClick={() => navigate('/login')}>
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto my-20 p-8 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-center text-purple-700">Register for Access</h2>
      {error && <div className="mb-4 text-red-600 font-semibold">{error}</div>}
      {warning && <div className="mb-4 text-yellow-600 font-semibold">{warning}</div>}
      <form onSubmit={handleSubmit} autoComplete="off" encType="multipart/form-data">
        {/* Avatar Upload */}
        <div className="mb-4 flex flex-col items-center">
          <label className="block mb-2 font-semibold">Profile Picture</label>
          <div className="mb-2">
            <input type="file" accept="image/*" name="avatar" onChange={handleChange} className="hidden" id="avatar-input" />
            <label htmlFor="avatar-input" className="cursor-pointer inline-flex items-center px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded shadow">
              {avatarPreview || form.avatar ? (
                <img src={avatarPreview || form.avatar} alt="Avatar" className="w-12 h-12 rounded-full object-cover border-2 border-purple-400" />
              ) : (
                <span>Upload Avatar</span>
              )}
            </label>
          </div>
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Name</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Phone</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
            maxLength={15}
            pattern="[0-9]+"
            placeholder="Enter your phone number"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              required
              autoComplete="new-password"
            />
            <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-purple-500">{showPassword ? 'Hide' : 'Show'}</button>
          </div>
          {pwStrength && (
            <div className={`mt-1 text-xs font-semibold ${pwStrength === 'Strong' ? 'text-green-600' : pwStrength === 'Medium' ? 'text-yellow-600' : 'text-red-600'}`}>Password Strength: {pwStrength}</div>
          )}
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Confirm Password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              required
              autoComplete="new-password"
            />
            <button type="button" tabIndex={-1} onClick={() => setShowConfirm(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-purple-500">{showConfirm ? 'Hide' : 'Show'}</button>
          </div>
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Date of Birth</label>
          <input
            type="date"
            name="dob"
            value={form.dob}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Gender</label>
          <select
            name="gender"
            value={form.gender}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer-not">Prefer not to say</option>
          </select>
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Address</label>
          <input
            type="text"
            name="address"
            value={form.address}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
            placeholder="Your address"
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-semibold">Preferred Contact</label>
          <select
            name="preferredContact"
            value={form.preferredContact}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded"
            required
          >
            <option value="">Select</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
        </div>
        <div className="mb-4 flex items-center">
          <input
            type="checkbox"
            name="terms"
            checked={form.terms}
            onChange={handleChange}
            className="mr-2"
            required
          />
          <span className="text-xs">I accept the <a href="#" className="underline text-purple-600">Terms & Conditions</a></span>
        </div>
        <button
          type="submit"
          className="w-full bg-purple-700 hover:bg-purple-800 text-white font-bold py-2 rounded transition"
        >
          Register
        </button>
      </form>
      <div className="mt-6 text-xs text-gray-400 text-center">
        <button className="underline" onClick={() => navigate('/login')}>Back to Login</button>
      </div>
    </div>
  );
};

export default Register; 